'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { sendOTP, signinOTP, getSession, useSession } from '@/lib/auth-client';
import { usersApi } from '@/lib/api/endpoints';
import { useItemTags } from '@/lib/hooks';
import type { TagsData } from '@/lib/api/types';
import { parseUserTags } from '@/lib/utils/tags';
import { cn } from '@/lib/utils';
import { GENDER_OPTIONS, NZ_REGIONS, USER_ROLE_OPTIONS } from '@sizzlenz/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '@/components/ui/input-otp';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Loader2, Mail, MapPin, Sparkles, X } from 'lucide-react';
import { RoleSelector } from '@/components/profile/RoleSelector';
interface SignInProps {
  onSuccess?: () => void;
  redirectTo?: string;
  completionRedirectTo?: string;
  preferFullscreenForNewUsers?: boolean;
  onBeforeRouteChange?: () => void;
  onOnboardingChange?: (isOnboarding: boolean) => void;
  initialEmail?: string;
  autoSubmitEmail?: boolean;
}
type FlowStep = 'email' | 'verify' | 'onboarding-profile' | 'onboarding-role' | 'onboarding-dietary' | 'onboarding-finished';
type OnboardingStepId = Extract<FlowStep, 'onboarding-profile' | 'onboarding-role' | 'onboarding-dietary' | 'onboarding-finished'>;
interface OnboardingState {
  firstName: string;
  lastName: string;
  gender?: string;
  birthYear: string;
  locations: string[];
  dietaryTags: string[];
  roles: string[];
  additionalDetails: string;
  isBrandOrBusiness: boolean;
}
interface OnboardingStepConfig {
  id: OnboardingStepId;
  title: string;
  description: string;
  optional: boolean;
}
const DEFAULT_TAGS_DATA: TagsData = {
  categories: [{
    id: 3,
    name: 'diet',
    items: [{
      id: 8,
      name: 'vegan'
    }, {
      id: 300,
      name: 'vegetarian'
    }, {
      id: 16,
      name: 'keto'
    }, {
      id: 102,
      name: 'high-protein'
    }]
  }, {
    id: 1,
    name: 'free from',
    items: [{
      id: 204,
      name: 'gluten-free'
    }, {
      id: 301,
      name: 'gluten free'
    }, {
      id: 302,
      name: 'dairy free'
    }, {
      id: 205,
      name: 'sugar-free'
    }]
  }]
};
const BASE_ONBOARDING_STEPS: OnboardingStepConfig[] = [{
  id: 'onboarding-profile',
  title: 'Tell us about you',
  description: 'Just the essentials to personalize your Sizzle experience.',
  optional: false
}, {
  id: 'onboarding-role',
  title: 'What brings you to Sizzle?',
  description: 'Help us understand how you plan to use the platform.',
  optional: false
}
// {
//   id: 'onboarding-dietary',
//   title: 'Dietary preferences',
//   description: 'Optional: choose preferences now, or skip and add them later.',
//   optional: true,
// },
];
const FINISHED_SIGNUP_STEP: OnboardingStepConfig = {
  id: 'onboarding-finished',
  title: 'You are signed up',
  description: 'Account created successfully. You can continue to Sizzle now.',
  optional: false
};
function splitName(name: string | null | undefined) {
  const value = (name || '').trim();
  if (!value) return {
    firstName: '',
    lastName: ''
  };
  const parts = value.split(/\s+/);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ')
  };
}
function getSessionUser(sessionResult: unknown) {
  const response = sessionResult as any;
  return response?.data?.user || response?.user || null;
}
function getDietaryOptions(tagsData: TagsData) {
  const allowed = new Set(['diet', 'free from']);
  return tagsData.categories.filter(category => allowed.has(category.name.toLowerCase().trim()));
}
function getDietaryTagSet(tagsData: TagsData) {
  return new Set(getDietaryOptions(tagsData).flatMap(category => category.items.map(item => item.name)));
}
function needsProfileOnboarding(user: any) {
  if (!user) return false;
  const {
    firstName,
    lastName
  } = splitName(user.name);
  if (!user?.name || typeof user.name !== 'string') return true;
}
function isLikelyNewUser(authResponse: any, sessionUser: any) {
  const responseUser = authResponse?.data?.user || authResponse?.user || sessionUser;
  const createdAt = responseUser?.createdAt || sessionUser?.createdAt;
  if (!createdAt) return false;
  const createdAtMs = new Date(createdAt).getTime();
  if (Number.isNaN(createdAtMs)) return false;
  const accountAgeMs = Date.now() - createdAtMs;
  if (accountAgeMs < 0 || accountAgeMs > 10 * 60 * 1000) return false;
  const {
    firstName,
    lastName
  } = splitName(responseUser?.name);
  const profileTags = parseUserTags(responseUser?.tags);
  return !firstName.trim() && !lastName.trim();
}
export function SignIn({
  onSuccess,
  redirectTo = '/',
  completionRedirectTo,
  preferFullscreenForNewUsers = false,
  onBeforeRouteChange,
  onOnboardingChange,
  initialEmail,
  autoSubmitEmail
}: SignInProps) {
  const router = useRouter();
  const [email, setEmail] = useState(initialEmail || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<FlowStep>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [shakeOtp, setShakeOtp] = useState(false);
  const inputOTPRef = useRef<HTMLInputElement>(null);
  const [success, setSuccess] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [showFinishedSignupStep, setShowFinishedSignupStep] = useState(false);
  const [didResumeOnboarding, setDidResumeOnboarding] = useState(false);
  const [baseTags, setBaseTags] = useState<string[]>([]);
  const didAutoSubmitRef = useRef(false);
  const [onboarding, setOnboarding] = useState<OnboardingState>({
    firstName: '',
    lastName: '',
    gender: undefined,
    birthYear: '',
    locations: [],
    dietaryTags: [],
    roles: [],
    additionalDetails: '',
    isBrandOrBusiness: false
  });
  const {
    data: session,
    refetch
  } = useSession();
  const {
    data: apiTagsData
  } = useItemTags();
  const tagsData = apiTagsData || DEFAULT_TAGS_DATA;
  const dietaryCategories = useMemo(() => getDietaryOptions(tagsData), [tagsData]);
  const dietaryTagSet = useMemo(() => getDietaryTagSet(tagsData), [tagsData]);
  const onboardingSteps = useMemo(() => showFinishedSignupStep ? [...BASE_ONBOARDING_STEPS, FINISHED_SIGNUP_STEP] : BASE_ONBOARDING_STEPS, [showFinishedSignupStep]);
  const isOnboardingStep = (currentStep: FlowStep): currentStep is OnboardingStepId => onboardingSteps.some(entry => entry.id === currentStep);
  const getOnboardingStepIndex = (currentStep_0: FlowStep) => {
    if (!isOnboardingStep(currentStep_0)) return -1;
    return onboardingSteps.findIndex(entry_0 => entry_0.id === currentStep_0);
  };
  const getNextOnboardingStep = (currentStep_1: FlowStep) => {
    const currentIndex = getOnboardingStepIndex(currentStep_1);
    if (currentIndex < 0) return null;
    return onboardingSteps[currentIndex + 1]?.id || null;
  };
  const getPreviousOnboardingStep = (currentStep_2: FlowStep) => {
    const currentIndex_0 = getOnboardingStepIndex(currentStep_2);
    if (currentIndex_0 <= 0) return null;
    return onboardingSteps[currentIndex_0 - 1]?.id || null;
  };
  const onboardingStepIndex = getOnboardingStepIndex(step);
  const onboardingStepConfig = onboardingStepIndex >= 0 ? onboardingSteps[onboardingStepIndex] : null;
  const onboardingProgress = onboardingStepIndex >= 0 ? onboardingStepIndex + 1 : 0;
  const onboardingQuery = router.query.onboarding;
  const newUserQuery = router.query.newUser;
  const shouldResumeOnboarding = typeof onboardingQuery === 'string' && onboardingQuery === '1' || Array.isArray(onboardingQuery) && onboardingQuery.includes('1');
  const shouldResumeAsNewUser = typeof newUserQuery === 'string' && newUserQuery === '1' || Array.isArray(newUserQuery) && newUserQuery.includes('1');
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    setTimeout(() => {
      // Find the closest scrollable ancestor (e.g. modal dialog) and scroll that,
      // falling back to window.scrollTo for full-page usage.
      const el = containerRef.current;
      if (el) {
        let scrollable: HTMLElement | null = el.parentElement;
        while (scrollable && scrollable !== document.body) {
          const {
            overflowY
          } = getComputedStyle(scrollable);
          if (overflowY === 'auto' || overflowY === 'scroll') {
            scrollable.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          scrollable = scrollable.parentElement;
        }
      }
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 50);
  };
  const initializeOnboardingFromUser = (user: any) => {
    const names = splitName(user?.name);
    const profileTags = parseUserTags(user?.tags);
    const existingRoles = user?.data?.roles as string[] || [];
    setOnboarding({
      firstName: names.firstName,
      lastName: names.lastName,
      gender: profileTags.gender,
      birthYear: profileTags.birthYear ? String(profileTags.birthYear) : '',
      locations: profileTags.locations,
      dietaryTags: (user?.tags || []).filter((tag: string) => dietaryTagSet.has(tag)),
      roles: existingRoles,
      additionalDetails: user?.data?.additionalDetails as string || '',
      isBrandOrBusiness: existingRoles.length > 0
    });
    setBaseTags(user?.tags || []);
  };
  useEffect(() => {
    if (!shouldResumeOnboarding || didResumeOnboarding || !session?.user) return;
    const currentUser = session.user as any;
    initializeOnboardingFromUser(currentUser);
    setShowFinishedSignupStep(shouldResumeAsNewUser);
    setStep(BASE_ONBOARDING_STEPS[0].id);
    setDidResumeOnboarding(true);
  }, [didResumeOnboarding, session, shouldResumeAsNewUser, shouldResumeOnboarding]);

  // Auto-enter onboarding if there's already a session user
  useEffect(() => {
    if (shouldResumeOnboarding || didResumeOnboarding) return;
    if (!session?.user) return;
    if (step !== 'email') return;
    const currentUser_0 = session.user as any;
    initializeOnboardingFromUser(currentUser_0);
    setStep(BASE_ONBOARDING_STEPS[0].id);
    setDidResumeOnboarding(true);
  }, [session, didResumeOnboarding, shouldResumeOnboarding, step]);

  // Auto-submit email when initialEmail + autoSubmitEmail are provided
  useEffect(() => {
    if (!autoSubmitEmail || !initialEmail || didAutoSubmitRef.current) return;
    if (step !== 'email') return;
    didAutoSubmitRef.current = true;
    const autoSend = async () => {
      setIsLoading(true);
      setError('');
      try {
        setCanResend(false);
        await sendOTP({
          email: initialEmail
        });
        setStep('verify');
        setSuccess('Verification code sent. Check your email.');
        setTimeout(() => inputOTPRef.current?.focus(), 300);
      } catch (err: any) {
        setError(err.message || 'Failed to send verification code');
      } finally {
        setIsLoading(false);
      }
    };
    autoSend();
  }, [autoSubmitEmail, initialEmail, step]);
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    setError('');
    try {
      setCanResend(false);
      await sendOTP({
        email
      });
      setStep('verify');
      setSuccess('Verification code sent. Check your email.');
      setTimeout(() => inputOTPRef.current.focus(), 300);
    } catch (err_0: any) {
      setError(err_0.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-submit when 6 digits are entered (typed or pasted)
  const autoSubmitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSubmittedCodeRef = useRef('');
  useEffect(() => {
    // Clear any pending auto-submit when code changes
    if (autoSubmitTimerRef.current) {
      clearTimeout(autoSubmitTimerRef.current);
      autoSubmitTimerRef.current = null;
    }
    if (step === 'verify' && verificationCode.length === 6 && !isLoading && verificationCode !== lastSubmittedCodeRef.current) {
      autoSubmitTimerRef.current = setTimeout(() => {
        autoSubmitTimerRef.current = null;
        lastSubmittedCodeRef.current = verificationCode;
        handleVerifyCode();
      }, 300);
    }
    return () => {
      if (autoSubmitTimerRef.current) {
        clearTimeout(autoSubmitTimerRef.current);
        autoSubmitTimerRef.current = null;
      }
    };
  }, [verificationCode, step, isLoading]);
  const handleVerifyCode = async (e_0?: React.FormEvent) => {
    e_0?.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      setOtpError('Please enter a valid 6-digit code');
      setShakeOtp(true);
      setTimeout(() => setShakeOtp(false), 500);
      return;
    }
    setIsLoading(true);
    setError('');
    setOtpError('');
    try {
      const response = await signinOTP({
        otp: verificationCode,
        email
      });
      if (response && typeof response === 'object' && 'error' in response && response.error) {
        setOtpError(response.error.message || 'Invalid verification code');
        setShakeOtp(true);
        setTimeout(() => setShakeOtp(false), 500);
        if (response.error.code === 'TOO_MANY_ATTEMPTS') {
          setCanResend(true);
        }
        return;
      }
      const sessionResult = await getSession();
      const sessionUser = getSessionUser(sessionResult);
      const newUser = isLikelyNewUser(response, sessionUser);
      if (newUser && preferFullscreenForNewUsers && router.pathname !== '/auth/signin') {
        const params = new URLSearchParams({
          onboarding: '1',
          newUser: '1'
        });
        if (redirectTo && redirectTo !== '/') {
          params.set('redirect', redirectTo);
        }
        const nextUrl = `/auth/signin?${params.toString()}`;
        onBeforeRouteChange?.();
        await router.push(nextUrl);
        return;
      }
      setShowFinishedSignupStep(newUser);
      if (needsProfileOnboarding(sessionUser)) {
        initializeOnboardingFromUser(sessionUser);
        setStep(onboardingSteps[0].id);
        setSuccess("Welcome to Sizzle. Let's finish your profile.");
        return;
      }
      setSuccess('Successfully signed in.');
      if (onSuccess) {
        onSuccess();
      } else {
        onBeforeRouteChange?.();
        await router.push(redirectTo);
      }
    } catch (err_1: any) {
      setOtpError(err_1.message || 'Invalid verification code');
      setShakeOtp(true);
      setTimeout(() => setShakeOtp(false), 500);
    } finally {
      setIsLoading(false);
    }
  };
  const handleBackToEmail = () => {
    setStep('email');
    setVerificationCode('');
    setError('');
    setSuccess('');
  };
  const handleAddLocation = (locationId: string) => {
    if (onboarding.locations.includes(locationId)) return;
    setOnboarding(prev => ({
      ...prev,
      locations: [...prev.locations, locationId]
    }));
  };
  const handleRemoveLocation = (locationId_0: string) => {
    setOnboarding(prev_0 => ({
      ...prev_0,
      locations: prev_0.locations.filter(location => location !== locationId_0)
    }));
  };
  const handleSaveProfileAndContinue = async () => {
    if (!onboarding.firstName.trim() || !onboarding.lastName.trim()) {
      setError('First name and last name are required.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const fullName = `${onboarding.firstName} ${onboarding.lastName}`.trim();
      const response_0 = await usersApi.updateProfile({
        name: fullName,
        gender: onboarding.gender || null,
        birthYear: onboarding.birthYear ? parseInt(onboarding.birthYear, 10) : null,
        locations: onboarding.locations
      });
      if (Array.isArray(response_0?.tags)) {
        setBaseTags(response_0.tags);
      }
      const nextStep = getNextOnboardingStep('onboarding-profile');
      if (nextStep) {
        setStep(nextStep);
        scrollToTop();
        return;
      }
      await completeSignIn();
    } catch (err_2: any) {
      setError(err_2.message || 'Could not save your profile details.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSaveRoleAndContinue = async () => {
    if (onboarding.isBrandOrBusiness && onboarding.roles.length === 0) {
      setError('Please select at least one role.');
      return;
    }
    if (onboarding.isBrandOrBusiness && !onboarding.additionalDetails.trim()) {
      setError('Please tell us more about your brands, businesses, or organisations.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await usersApi.updateProfile({
        data: {
          roles: onboarding.isBrandOrBusiness ? onboarding.roles : [],
          additionalDetails: onboarding.isBrandOrBusiness ? onboarding.additionalDetails.trim() || undefined : undefined,
          isBrandOrBusiness: onboarding.isBrandOrBusiness
        }
      });
      const nextStep_0 = getNextOnboardingStep('onboarding-role');
      if (nextStep_0) {
        setStep(nextStep_0);
        scrollToTop();
        return;
      }
      await completeSignIn();
    } catch (err_3: any) {
      setError(err_3.message || 'Could not save your information.');
    } finally {
      setIsLoading(false);
    }
  };
  const completeSignIn = async (nextTags?: string[], overrideRedirect?: string) => {
    if (nextTags) {
      await usersApi.updateProfile({
        tags: nextTags
      });
    }
    await refetch();
    setSuccess("Welcome to Sizzle. You're all set.");
    if (onSuccess) {
      onSuccess();
      return;
    }
    onBeforeRouteChange?.();
    await router.push(overrideRedirect || redirectTo);
  };
  const goToNextOnboardingStepOrComplete = async (currentStep_3: OnboardingStepId) => {
    const nextStep_1 = getNextOnboardingStep(currentStep_3);
    if (nextStep_1) {
      setStep(nextStep_1);
      scrollToTop();
      return;
    }
    await completeSignIn();
  };
  const handleSaveDietaryAndFinish = async () => {
    setIsLoading(true);
    setError('');
    try {
      const sourceTags = baseTags.length > 0 ? baseTags : (session?.user as any)?.tags || [];
      const preserved = sourceTags.filter((tag_0: string) => !dietaryTagSet.has(tag_0));
      const uniqueDietary = Array.from(new Set(onboarding.dietaryTags));
      const mergedTags = [...preserved, ...uniqueDietary];
      await usersApi.updateProfile({
        tags: mergedTags
      });
      await goToNextOnboardingStepOrComplete('onboarding-dietary');
    } catch (err_4: any) {
      setError(err_4.message || 'Could not save dietary preferences.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSkipDietary = async () => {
    setIsLoading(true);
    setError('');
    try {
      await goToNextOnboardingStepOrComplete('onboarding-dietary');
    } catch (err_5: any) {
      setError(err_5.message || 'Could not finish onboarding.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSkipCurrentOptionalStep = async () => {
    if (step === 'onboarding-dietary') {
      await handleSkipDietary();
      return;
    }
  };
  const toggleDietary = (tagName: string) => {
    setOnboarding(prev_1 => ({
      ...prev_1,
      dietaryTags: prev_1.dietaryTags.includes(tagName) ? prev_1.dietaryTags.filter(tag_1 => tag_1 !== tagName) : [...prev_1.dietaryTags, tagName]
    }));
  };
  const showAuthShell = step === 'email' || step === 'verify';
  useEffect(() => {
    onOnboardingChange?.(!showAuthShell);
  }, [showAuthShell, onOnboardingChange]);
  return <div ref={containerRef} className="mx-auto w-full max-w-full min-w-0">
      <div className="rounded-3xl border border-orange-100/80 bg-white/95 shadow-[0_20px_60px_-35px_rgba(249,115,22,0.45)] backdrop-blur">
        <div className="border-b border-orange-100 px-6 py-6 sm:px-8">
          {showAuthShell ? <>
              <Badge className="mb-4 bg-orange-100 text-orange-700 hover:bg-orange-100">
                Sign in or sign up
              </Badge>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                Get started with Sizzle
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                {step === 'email' ? 'Enter your email to sign in or create an account.' : `Enter the 6-digit code we sent to ${email}.`}
              </p>
            </> : <>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                {onboardingStepConfig?.title}
              </h1>
              <p className="mt-2 text-sm text-slate-500">{onboardingStepConfig?.description}</p>
              <div className="mt-4">
                <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                  <span>
                    Step {onboardingProgress} of {onboardingSteps.length}
                  </span>
                  <span>{Math.round(onboardingProgress / onboardingSteps.length * 100)}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-orange-100">
                  <div className="h-full rounded-full bg-orange-500 transition-all" style={{
                width: `${onboardingProgress / onboardingSteps.length * 100}%`
              }} />
                </div>
              </div>
            </>}
        </div>

        <div className="px-6 py-6 sm:px-8">
          {error && <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
              {canResend && <div className="mt-3 text-right">
                  <Button variant="outline" size="sm" onClick={handleSend} disabled={isLoading}>
                    Resend code
                  </Button>
                </div>}
            </Alert>}

          {success && step !== 'onboarding-finished' && <Alert className="mb-4 border-emerald-200 bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>}

          {step === 'email' && <form onSubmit={handleSend} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={e_1 => setEmail(e_1.target.value)} required disabled={isLoading} className="h-12 rounded-xl border-slate-200 pl-9" />
                </div>
              </div>

              <Button type="submit" className="h-12 w-full rounded-xl bg-orange-500 text-base hover:bg-orange-600" disabled={isLoading || !email}>
                {isLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending code...
                  </> : 'Continue'}
              </Button>
            </form>}

          {step === 'verify' && <form onSubmit={handleVerifyCode} className="space-y-6">
              <div className={cn('space-y-3', shakeOtp && 'animate-[shake_0.4s_ease-in-out]')}>
                <Label htmlFor="code">Verification code</Label>
                <InputOTP ref={inputOTPRef} id="code" maxLength={6} value={verificationCode} onChange={value => {
              setVerificationCode(value);
              if (otpError) setOtpError('');
            }} containerClassName="justify-center" className="text-base md:text-sm">
                  <InputOTPGroup>
                    <OTPSlot index={0} />
                    <OTPSlot index={1} />
                    <OTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator className="mx-1" />
                  <InputOTPGroup>
                    <OTPSlot index={3} />
                    <OTPSlot index={4} />
                    <OTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                {otpError ? <p className="text-center text-sm font-medium text-red-500">{otpError}</p> : <p className="text-center text-xs text-slate-500">Code sent to {email}</p>}
              </div>

              <div className="space-y-2">
                <Button type="submit" className="h-12 w-full rounded-xl bg-orange-500 text-base hover:bg-orange-600" disabled={isLoading || verificationCode.length !== 6}>
                  {isLoading ? <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </> : 'Verify'}
                </Button>

                <Button type="button" variant="ghost" className="h-11 w-full rounded-xl" onClick={handleBackToEmail} disabled={isLoading}>
                  Back to email
                </Button>
              </div>
            </form>}

          {step === 'onboarding-profile' && <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First name *</Label>
                  <Input id="firstName" value={onboarding.firstName} onChange={e_2 => setOnboarding(prev_2 => ({
                ...prev_2,
                firstName: e_2.target.value
              }))} className="h-11 rounded-xl" disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last name *</Label>
                  <Input id="lastName" value={onboarding.lastName} onChange={e_3 => setOnboarding(prev_3 => ({
                ...prev_3,
                lastName: e_3.target.value
              }))} className="h-11 rounded-xl" disabled={isLoading} />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender (optional)</Label>
                  <Select value={onboarding.gender} onValueChange={value_0 => setOnboarding(prev_4 => ({
                ...prev_4,
                gender: value_0
              }))} disabled={isLoading}>
                    <SelectTrigger id="gender" className="h-11 w-full rounded-xl">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map(option => <SelectItem key={option.code} value={option.code}>
                          {option.label}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birthYear">Year of birth (optional)</Label>
                  <Input id="birthYear" type="number" min="1900" max={new Date().getFullYear()} placeholder="e.g. 1990" value={onboarding.birthYear} onChange={e_4 => setOnboarding(prev_5 => ({
                ...prev_5,
                birthYear: e_4.target.value
              }))} className="h-11 rounded-xl" disabled={isLoading} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location (optional)</Label>
                <Select value="" onValueChange={handleAddLocation} disabled={isLoading}>
                  <SelectTrigger id="location" className="h-11 w-full rounded-xl">
                    <SelectValue placeholder="Add location" />
                  </SelectTrigger>
                  <SelectContent>
                    {NZ_REGIONS.filter(region => !onboarding.locations.includes(region.id)).map(region_0 => <SelectItem key={region_0.id} value={region_0.id}>
                          {region_0.name}
                        </SelectItem>)}
                  </SelectContent>
                </Select>

                {onboarding.locations.length > 0 && <div className="flex flex-wrap gap-2 pt-2">
                    {onboarding.locations.map(locationId_1 => {
                const region_1 = NZ_REGIONS.find(entry_1 => entry_1.id === locationId_1);
                return <span key={locationId_1} className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-sm text-orange-700">
                          <MapPin className="h-3.5 w-3.5" />
                          {region_1?.name || locationId_1}
                          <button type="button" className="rounded-full p-0.5 transition-colors hover:bg-orange-100" onClick={() => handleRemoveLocation(locationId_1)} aria-label={`Remove ${region_1?.name || locationId_1}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>;
              })}
                  </div>}
              </div>

              <Button onClick={handleSaveProfileAndContinue} className="h-12 w-full rounded-xl bg-orange-500 text-base hover:bg-orange-600" disabled={isLoading}>
                {isLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </> : 'Continue'}
              </Button>
            </div>}

          {step === 'onboarding-role' && <div className="space-y-6">
              <button type="button" onClick={() => {
            const next = !onboarding.isBrandOrBusiness;
            setOnboarding(prev_6 => ({
              ...prev_6,
              isBrandOrBusiness: next,
              // Clear roles when unchecking
              ...(!next ? {
                roles: [],
                additionalDetails: ''
              } : {})
            }));
            setError('');
          }} disabled={isLoading} className={cn('w-full rounded-xl border px-4 py-4 text-left transition-all', onboarding.isBrandOrBusiness ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-white hover:border-orange-200 hover:bg-orange-50/40', isLoading && 'cursor-not-allowed opacity-50')}>
                <div className="flex items-start gap-3">
                  <div className={cn('mt-0.5 flex h-5 w-5 items-center justify-center rounded border-2 transition-colors', onboarding.isBrandOrBusiness ? 'border-orange-500 bg-orange-500' : 'border-slate-300 bg-white')}>
                    {onboarding.isBrandOrBusiness && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className={cn('text-sm font-medium', onboarding.isBrandOrBusiness ? 'text-orange-700' : 'text-slate-900')}>
                      I represent a brand or business or provider in the FMCG/CPG industry, or also
                      looking for workplace or space or hosting or host opportunities
                    </div>
                  </div>
                </div>
              </button>

              {onboarding.isBrandOrBusiness && <RoleSelector selectedRoles={onboarding.roles} additionalDetails={onboarding.additionalDetails} onRolesChange={roles => setOnboarding(prev_7 => ({
            ...prev_7,
            roles
          }))} onAdditionalDetailsChange={names_0 => setOnboarding(prev_8 => ({
            ...prev_8,
            additionalDetails: names_0
          }))} requireAdditionalDetails disabled={isLoading} />}

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button variant="ghost" className="h-11 rounded-xl" onClick={() => {
              const previousStep = getPreviousOnboardingStep(step);
              if (previousStep) {
                setStep(previousStep);
                scrollToTop();
              }
            }} disabled={isLoading}>
                  Back
                </Button>
                <Button className="h-11 rounded-xl bg-orange-500 hover:bg-orange-600" onClick={handleSaveRoleAndContinue} disabled={isLoading}>
                  {isLoading ? <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </> : 'Continue'}
                </Button>
              </div>
            </div>}

          {step === 'onboarding-dietary' && <div className="space-y-6">
              <div className="space-y-5">
                {dietaryCategories.map(category => <section key={category.id} className="space-y-2">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                      {category.name}
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {category.items.map(item => {
                  const selected = onboarding.dietaryTags.includes(item.name);
                  return <button key={item.id} type="button" onClick={() => toggleDietary(item.name)} className={cn('rounded-xl border px-3 py-3 text-left text-sm font-medium transition-all', selected ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-slate-200 bg-white text-slate-700 hover:border-orange-200 hover:bg-orange-50/40')}>
                            {item.name}
                          </button>;
                })}
                    </div>
                  </section>)}
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Button variant="ghost" className="h-11 rounded-xl sm:col-span-1" onClick={() => {
              const previousStep_0 = getPreviousOnboardingStep(step);
              if (previousStep_0) {
                setStep(previousStep_0);
                scrollToTop();
              }
            }} disabled={isLoading}>
                  Back
                </Button>
                {onboardingStepConfig?.optional ? <Button variant="outline" className="h-11 rounded-xl border-slate-200 sm:col-span-1" onClick={handleSkipCurrentOptionalStep} disabled={isLoading}>
                    Skip
                  </Button> : <div />}
                <Button className="h-11 rounded-xl bg-orange-500 hover:bg-orange-600 sm:col-span-1" onClick={handleSaveDietaryAndFinish} disabled={isLoading}>
                  {isLoading ? <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Finishing...
                    </> : 'Finish'}
                </Button>
              </div>
            </div>}

          {step === 'onboarding-finished' && <div className="space-y-6">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                <h2 className="mt-1 text-2xl font-bold text-slate-900">Sign up complete!</h2>
                <p className="mt-2 text-sm text-slate-600">
                  Thanks for signing up! We are looking forward to helping you Sizzle! You will
                  receive a confirmation email when your account has been approved.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button variant="outline" className="h-11 rounded-xl cursor-pointer" onClick={() => {
              const previousStep_1 = getPreviousOnboardingStep(step);
              if (previousStep_1) {
                setStep(previousStep_1);
                scrollToTop();
              }
            }} disabled={isLoading}>
                  Back
                </Button>
                <Button className="h-11 rounded-xl bg-orange-500 hover:bg-orange-600 cursor-pointer" onClick={() => completeSignIn(undefined, completionRedirectTo)} disabled={isLoading}>
                  Continue to Sizzle
                </Button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}
function OTPSlot(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props.index) {
    t0 = <InputOTPSlot index={props.index} className="h-12 w-12 text-[2rem]" />;
    $[0] = props.index;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}